import React, { useEffect, useMemo, useState } from "react";
import { AimOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import InputField from "../../../Reuseable/InputField/InputField";
import CustomSelect from "../../../Reuseable/Select/Select";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import PanelCard from "../../../Pages/DashboardPage/components/PanelCard";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";
import copyIcon from "../../../assets/Icons/copyIconTrust.svg";
import "./TargetAddNewTarget.scss";
import addIcon from "../../../assets/Icons/addIconTrust.svg";
import { ReactComponent as TrashIcon } from "../../../assets/Icons/trashcan.svg";
import RangePickerWithPresets from "../../../Reuseable/RengePicker/RangePickerWithPresets";
import { TARGET_TYPE_CHOICES } from "../targetTypes";
import { fetchUserFilters } from "../../../store/filters";
import { fetchAgencyUsers } from "../../../store/agencySlice";
import { createTargets } from "../../../assets/api/targets";

const EMPTY_ROW = { user: "", portfolio: "", target: "", value: "", startDate: "", endDate: "" };

const TargetAddNewTarget = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const { filter_list } = useSelector((state) => state.filters) || {};
  const { user_list, user_loading } = useSelector((state) => state.agency);

  const [targetRows, setTargetRows] = useState([{ ...EMPTY_ROW }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchUserFilters());
    /* one page big enough to hold the whole agency - this list feeds a
       dropdown, not a paged table */
    dispatch(fetchAgencyUsers({ page: 1, page_size: 200 }));
  }, [dispatch]);

  /* was hardcoded to "User 1" / "User 2", so nothing selectable mapped to a
     real user id */
  const userOptions = useMemo(() => {
    const rows = user_list?.results || [];
    return rows
      .filter((row) => row?.id)
      .map((row) => ({
        value: row.id,
        label: row.full_name || row.email || `User ${row.id}`,
      }));
  }, [user_list]);

  const portfolioOptions = useMemo(() => {
    const rows =
      filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return rows.map((row) => ({
      value: row.id ?? row,
      label: row.name ?? row.key ?? row,
    }));
  }, [filter_list]);

  const targetOptions = TARGET_TYPE_CHOICES;

  const handleRowChange = (index, field, value) => {
    const updated = [...targetRows];
    updated[index][field] = value;
    setTargetRows(updated);
  };

  const addRow = (index) => {
    const updatedRows = [...targetRows];
    updatedRows.splice(index + 1, 0, { ...EMPTY_ROW });
    setTargetRows(updatedRows);
  };

  /* The copy button was wired to handleSave, so it did nothing. Its icon
     says duplicate, so it duplicates. */
  const duplicateRow = (index) => {
    const updatedRows = [...targetRows];
    updatedRows.splice(index + 1, 0, { ...targetRows[index] });
    setTargetRows(updatedRows);
  };

  const removeRow = (index) => {
    const updated = [...targetRows];
    updated.splice(index, 1);
    setTargetRows(updated);
  };

  /* Reported before the request goes out, naming the row, so a rejection is
     not the first time you learn a field is missing. */
  const findProblem = () => {

    const seen = new Map();
    for (let i = 0; i < targetRows.length; i += 1) {
      const row = targetRows[i];
      const label = `Row ${i + 1}`;

      if (!row.user) return `${label}: select a user.`;
      if (!row.portfolio) return `${label}: select a portfolio.`;
      if (!row.target) return `${label}: select a target.`;

      const value = Number(row.value);
      if (row.value === "" || !Number.isFinite(value) || value <= 0) {
        return `${label}: enter a target value greater than zero.`;
      }

      if (!row.startDate || !row.endDate) {
        return `${label}: select the duration for this target.`;
      }

      /* the same user, portfolio and target twice in one batch is a
         contradiction, and the server would reject the whole thing */
      const fingerprint = `${row.user}|${row.portfolio}|${row.target}`;
      if (seen.has(fingerprint)) {
        return `${label} repeats the same user, portfolio and target as row ${
          seen.get(fingerprint) + 1
        }.`;
      }
      seen.set(fingerprint, i);
    }

    return null;
  };

  const handleSave = async () => {
    const problem = findProblem();
    if (problem) {
      CustomNotification.show(problem, "error");
      return;
    }

    const payload = {
      targets: targetRows.map((row) => ({
        user: Number(row.user),
        portfolio: Number(row.portfolio),
        target_type: row.target,
        target_value: Number(row.value),
        start_date: dayjs(row.startDate).format("YYYY-MM-DD"),
        end_date: dayjs(row.endDate).format("YYYY-MM-DD"),
      })),
    };

    try {
      setSaving(true);
      const res = await createTargets(payload, token);
      if (res?.status === 200 || res?.status === 201) {
        CustomNotification.show(
          payload.targets.length === 1
            ? "Target created successfully"
            : `${payload.targets.length} targets created successfully`,
          "success"
        );
        navigate("/target-achievement");
      }
    } catch (error) {
      CustomNotification.show(
        error?.message || error?.detail || "Failed to create targets",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/target-achievement");
  };

  return (
    <PanelCard
      title="Add New Target"
      icon={<AimOutlined />}
      accent="brand"
      className="targetAddNewTarget"
    >
      {/* ---- who gets what ---- */}
      <div className="targetRows">
        <header className="targetRows__head">
          <span className="targetRows__title">Assignments</span>
          <span className="targetRows__count">
            {targetRows.length} {targetRows.length === 1 ? "row" : "rows"}
          </span>
        </header>

        {targetRows.map((row, index) => (
          <div className="row-group" key={index}>
            <span className="row-index" aria-hidden="true">
              {index + 1}
            </span>

            <div className="form-row">
              <CustomSelect
                label="Users"
                placeholder="Select"
                value={row.user || undefined}
                options={userOptions}
                loading={user_loading}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(val) => handleRowChange(index, "user", val)}
              />
              <CustomSelect
                label="Portfolio"
                placeholder="Select"
                value={row.portfolio || undefined}
                options={portfolioOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(val) => handleRowChange(index, "portfolio", val)}
              />
              <CustomSelect
                label="Select Target"
                placeholder="Select"
                value={row.target || undefined}
                options={targetOptions}
                onChange={(val) => handleRowChange(index, "target", val)}
              />
              <InputField
                label="Target Value"
                placeholder="Enter target value"
                type="number"
                value={row.value}
                onChange={(e) =>
                  handleRowChange(index, "value", e.target.value)
                }
              />
              <div className="targetField">
                <span className="targetField__label">Duration</span>
                <RangePickerWithPresets
                  value={row.startDate && row.endDate ? [row.startDate, row.endDate] : []}
                  onDateChange={(startDate, endDate) => {
                    handleRowChange(index, "startDate", startDate);
                    handleRowChange(index, "endDate", endDate);
                  }}
                />
              </div>
            </div>

            <div className="row-actions">
              <Tooltip title="Duplicate row">
                <button
                  type="button"
                  className="icon-btn copy"
                  onClick={() => duplicateRow(index)}
                >
                  <img src={copyIcon} alt="" />
                </button>
              </Tooltip>

              {index === 0 ? (
                <Tooltip title="Add row">
                  <button
                    type="button"
                    className="icon-btn add"
                    onClick={() => addRow(index)}
                  >
                    <img src={addIcon} alt="" />
                  </button>
                </Tooltip>
              ) : (
                targetRows.length > 1 && (
                  <Tooltip title="Remove row">
                    <button
                      type="button"
                      className="icon-btn delete"
                      onClick={() => removeRow(index)}
                    >
                      <TrashIcon />
                    </button>
                  </Tooltip>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="footer-buttons">
        {/* Cancel had handleCancel defined but never attached */}
        <button type="button" className="cancelBtn" onClick={handleCancel}>
          Cancel
        </button>
        <NormalButton
          onClick={handleSave}
          loading={saving}
          className="submit-btn"
        >
          Save Changes
        </NormalButton>
      </div>
    </PanelCard>
  );
};

export default TargetAddNewTarget;
